import { useState, useEffect, useRef } from "react";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (address: string, lat: number, lng: number) => void;
  id?: string;
  placeholder?: string;
}

const MIN_QUERY_LENGTH = 3;

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  id,
  placeholder,
}: AddressAutocompleteProps) {
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [resultsForValue, setResultsForValue] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dismissedValue, setDismissedValue] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const skipNextSearch = useRef(false);

  const canQuery = value.length >= MIN_QUERY_LENGTH;
  const open =
    canQuery && results.length > 0 && resultsForValue === value && dismissedValue !== value;

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!canQuery) return;

    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const params = new URLSearchParams({
          q: value,
          format: "json",
          limit: "5",
          countrycodes: "pl",
          addressdetails: "0",
        });
        const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
          headers: { "Accept-Language": "pl" },
          signal: controller.signal,
        });
        const data: NominatimResult[] = await res.json();
        setResults(data);
        setResultsForValue(value);
      } catch (err) {
        if (err instanceof Error && err.name !== "AbortError") {
          // silently ignore network errors, but not aborts
        }
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [value, canQuery]);

  const handleSelect = (result: NominatimResult) => {
    skipNextSearch.current = true;
    onSelect(result.display_name, parseFloat(result.lat), parseFloat(result.lon));
    setDismissedValue(result.display_name);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setDismissedValue(value);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Input
            id={id}
            value={value}
            onChange={(e) => {
              setDismissedValue(null);
              onChange(e.target.value);
            }}
            placeholder={placeholder}
            autoComplete="off"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={() => setDismissedValue(value)}
        sideOffset={4}
      >
        <ul className="max-h-60 overflow-y-auto py-1">
          {results.map((result) => (
            <li key={result.place_id}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(result);
                }}
              >
                {result.display_name}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
